import React, { useEffect, useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { celebrateHabitCompleted } from '../../feedback/celebrate'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  fetchHabitWeek,
  selectActiveHabits,
  selectCompletionsForWeek,
  setHabitCompletion,
} from '../../redux/habits'
import { currentWeekStartKey, weekDateKeys } from '../../redux/habits/week'
import { todayKey } from '../../redux/tasks'
import styles from './HabitsSection.module.css'

const WEEKDAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const

const CheckIcon = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <motion.path
      d="M5 12l4 4L19 7"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={reduceMotion ? { pathLength: 1, opacity: 1 } : { pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { pathLength: { duration: 0.28, ease: 'easeOut' }, opacity: { duration: 0.12 } }
      }
    />
  </svg>
)

export type HabitsSectionProps = {
  /** Monday yyyy-MM-dd for the week to show. Omit to follow the current calendar week (updates every minute). */
  weekStart?: string
}

const HabitsSection = ({ weekStart: weekStartProp }: HabitsSectionProps) => {
  const dispatch = useAppDispatch()
  const reduceMotion = useReducedMotion()
  const isControlled = weekStartProp !== undefined
  const [weekStartLocal, setWeekStartLocal] = useState(() => currentWeekStartKey())
  const weekStart = isControlled ? weekStartProp : weekStartLocal
  const dateKeys = useMemo(() => weekDateKeys(weekStart), [weekStart])
  const today = todayKey()

  const activeHabits = useAppSelector(selectActiveHabits)
  const completionsForWeek = useAppSelector(selectCompletionsForWeek(weekStart))
  const error = useAppSelector((s) => s.habits.error)

  useEffect(() => {
    if (isControlled) return
    setWeekStartLocal(currentWeekStartKey())
    const id = window.setInterval(() => setWeekStartLocal(currentWeekStartKey()), 60_000)
    return () => window.clearInterval(id)
  }, [isControlled])

  // Habits list is loaded by `HabitsBootstrap` in `App.tsx` (and `/habits` on navigation).
  // Do not `fetchHabits()` here: combined with the dashboard skeleton, that caused a mount loop.

  useEffect(() => {
    void dispatch(fetchHabitWeek(weekStart))
  }, [dispatch, weekStart])

  const dayOfMonth = (dateKey: string) => {
    const [, , d] = dateKey.split('-')
    return String(parseInt(d ?? '1', 10))
  }

  const onToggleCell = (
    habitId: string,
    habitTitle: string,
    date: string,
    done: boolean
  ) => {
    if (date > today) return
    if (!done) {
      celebrateHabitCompleted(habitTitle)
    }
    void dispatch(setHabitCompletion({ habitId, date, completed: !done, weekStart }))
  }

  return (
    <div className={styles.root}>
      <div
        className={styles.panel}
        role="region"
        aria-label="Weekly habit completions"
      >
        {error ? <p className={styles.errorBanner}>{error}</p> : null}

        {activeHabits.length === 0 ? (
          <p className={styles.empty}>
            No active habits yet.{' '}
            <Link to="/habits" className={styles.emptyLink}>
              Add habits
            </Link>
          </p>
        ) : (
          <div className={styles.grid} role="grid" aria-label="Weekly habit completions">
            <div className={styles.gridHeaderRow} role="row">
              <div className={styles.gridCorner} role="columnheader" />
              {dateKeys.map((dk, i) => (
                <div
                  key={dk}
                  className={`${styles.gridHeadCell} ${dk === today ? styles.gridHeadCellToday : ''}`}
                  role="columnheader"
                >
                  {dk === today ? (
                    <span className={styles.gridHeadTodayDot} aria-hidden />
                  ) : null}
                  <span className={styles.gridHeadLetter}>{WEEKDAY_LETTERS[i]}</span>
                  <span className={styles.gridHeadDate}>{dayOfMonth(dk)}</span>
                </div>
              ))}
            </div>

            {activeHabits.map((h) => {
              const datesDone = new Set(completionsForWeek[h.id] ?? [])
              return (
                <div key={h.id} className={styles.gridRow} role="row">
                  <div className={styles.gridHabitCell} role="rowheader">
                    <div className={styles.gridHabitText}>
                      <span className={styles.rowTitle}>{h.title}</span>
                      {h.category ? (
                        <span className={styles.category}>{h.category}</span>
                      ) : null}
                    </div>
                  </div>
                  {dateKeys.map((dk) => {
                    const done = datesDone.has(dk)
                    const isFuture = dk > today
                    const futureLabel = `Cannot log ${h.title} for ${dk} — that day has not arrived yet`
                    const pastTodayLabel = done
                      ? `Mark ${h.title} not done on ${dk}`
                      : `Mark ${h.title} done on ${dk}`
                    return (
                      <div key={dk} className={styles.gridCellWrap} role="gridcell">
                        <motion.button
                          type="button"
                          disabled={isFuture}
                          className={`${styles.checkBtn} ${done ? styles.checkBtnDone : ''} ${
                            isFuture && !done ? styles.checkBtnFuture : ''
                          } ${dk === today ? styles.checkBtnTodayCol : ''}`}
                          aria-pressed={done}
                          aria-label={isFuture ? futureLabel : pastTodayLabel}
                          title={isFuture ? 'You can only log habits for today or past days' : undefined}
                          onClick={() => onToggleCell(h.id, h.title, dk, done)}
                          whileTap={reduceMotion || isFuture ? undefined : { scale: 0.85 }}
                        >
                          {done ? <CheckIcon reduceMotion={!!reduceMotion} /> : null}
                        </motion.button>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default HabitsSection
